import { mainLogger as log } from "@/app/logger"
import { LocalStorage } from "@/app/storage"
import { Interval } from "@/app/types"
import { isNotNull } from "@/app/utils"
import { getToken } from "@/lib/get_token"

const API2URL = `${process.env.API_URL}/api/v2`
const HEADERS = {
  Accept: "application/json",
  "Content-Type": "application/json",
}

// set the cache interval to 4 hours
const ONE_HOUR_MILLIS = 60 * 60 * 1000
const CACHE_INTERVAL = 4 * ONE_HOUR_MILLIS

type ApiV2OptionsProps = {
  key: string // The URL and KEY are both unique for a given request and they are usually very similar - The intention behind keeping them separate is that the key could be shorter than the URL so the localstorage uses the "key" as the unique key instead of the URL.
  cacheLS?: boolean
  method?: string
  body?: any
  compressData?: boolean
  queryParam?: Record<string, any>
  isAuthNeeded?: boolean
}

type ApiV2Props = {
  url: string // URL Path in API endpoint
  options: ApiV2OptionsProps
}

/**
 * Performs an asynchronous API call to the specified URL and optionally caches the response.
 *
 * @param key - A unique key used for caching and logging the API call.
 * @param url - The endpoint URL to which the API call is made.
 * @param cacheLS - Optional. If set to true, enables caching of the response in local storage.
 * @param method - Optional. The HTTP method to be used for the API call, standard is "GET".
 * @param body - Optional. In case the API call is a POST request, the request body.
 * @param compressData - If set to true, compresses the JSON response before caching it, standard is true.
 * @param queryParam - Query parameters to be appended to the URL.
 * @param isAuthNeeded - Control authentication to be sent in Fetch API header.
 * @returns A promise that resolves to the API response data. If an error occurs during the API call,
 *                          or if the response is not OK, the function returns null. If cached data is available
 *                          and valid, it returns the cached data instead of making a new API call.
 *
 * The function first checks for the availability of cached data corresponding to the given key. If found and valid,
 * it returns this data. Otherwise, it proceeds with a new API call using the `fetch` function. After receiving the
 * response, the function checks for a successful response status. If successful, it processes and optionally caches
 * the JSON response. In case of any errors, whether in network response, JSON processing, or caching, the function
 * logs the error and returns null.
 */
export async function _apiV2({
  url,
  options: {
    key,
    cacheLS = false,
    method,
    compressData = true,
    body = undefined,
    queryParam = undefined,
    isAuthNeeded = true,
  },
}: ApiV2Props): Promise<any> {
  // for now, data only expires by time
  const data =
    cacheLS && LocalStorage.isAvailable() && method === "GET" ?
      LocalStorage.get(key, compressData)
    : null

  if (isNotNull(data)) return Promise.resolve(data)

  let apiToken = ""

  if (isAuthNeeded) apiToken = await getToken()

  const options: RequestInit = {
    headers: { ...HEADERS },
    method: method,
    cache: "no-store",
  }

  if (isAuthNeeded && options.headers && apiToken !== "")
    options.headers["Authorization"] = `Bearer ${apiToken}`

  if (body) {
    options.body = JSON.stringify(body)
  }

  const apiUrl = new URL(`${API2URL}${url}`)

  if (queryParam) {
    Object.entries(queryParam).forEach(([key, value]) => {
      apiUrl.searchParams.append(key, value)
    })
  }

  return fetch(apiUrl, options)
    .then((response) => {
      const statusMessage = `code:${response.status}(${response.statusText}), type:${response.type}`
      if (!response.ok) {
        log.error("API error in", key, "with", statusMessage)
        throw Error(`"API error in ${key} with ${statusMessage}`) // dont propagate the error, return null
      }
      return response.json()
    })
    .then((json) => {
      log.debug("APIV2 Loaded", key, "with", json)
      try {
        if (cacheLS)
          LocalStorage.set(key, json, Date.now() + CACHE_INTERVAL, compressData)
      } catch (err) {
        // log and ignore any errors while saving in LS
        log.warn(
          "Could not store API response in LocalStorage for key",
          key,
          ", err",
          err
        )
      }
      return json
    })
    .catch((error) => {
      log.error("APIV2 Error in", key, "as", error)
      return null // dont propagate the error, return null
    })
}

type ApiV2IntervalProps = {
  url: string
  interval: string
  options: ApiV2OptionsProps
}

export async function _apiV2_Interval({
  url,
  interval,
  options,
}: ApiV2IntervalProps) {
  if (!Interval.isValidInterval(interval)) {
    log.error("Invalid interval:", interval, "while calling API", options.key)
    return Promise.reject(null)
  }
  return _apiV2_get(url, options)
}

const API_CMS_URL = `${process.env.API_CMS_URL}/api/cms`

export async function _apiV2_CMS({
  url,
  options: {
    key,
    method = "GET",
    cacheLS = false,
    compressData = true,
    queryParam = undefined,
    isAuthNeeded = true,
  },
}: ApiV2Props): Promise<any> {
  // for now, data only expires by time
  const data =
    cacheLS && LocalStorage.isAvailable() && method === "GET" ?
      LocalStorage.get(key, compressData)
    : null

  if (isNotNull(data)) return Promise.resolve(data)

  let apiToken = ""

  if (isAuthNeeded) {
    apiToken = await getToken()
  }

  const options: RequestInit = {
    headers: { ...HEADERS },
    method: method,
    cache: "no-store",
  }

  if (isAuthNeeded && options.headers) {
    options.headers["Authorization"] = `Bearer ${apiToken}`
  }

  const apiUrl = new URL(`${API_CMS_URL}${url}`)

  if (queryParam) {
    Object.entries(queryParam).forEach(([key, value]) => {
      apiUrl.searchParams.append(key, value)
    })
  }

  return fetch(apiUrl, options)
    .then((response) => {
      const statusMessage = `code:${response.status}(${response.statusText}), type:${response.type}`
      if (!response.ok) {
        log.error("API CMS error in", key, "with", statusMessage)
        throw Error(`"API CMS error in ${key} with ${statusMessage}`)
      }
      return response.json()
    })
    .then((json) => {
      log.debug("APIV2 CMS Loaded", key, "with", json)
      try {
        if (cacheLS)
          LocalStorage.set(key, json, Date.now() + CACHE_INTERVAL, compressData)
      } catch (err) {
        // log and ignore any errors while saving in LS
        log.warn(
          "Could not store API CMS response in LocalStorage for key",
          key,
          ", err",
          err
        )
      }
      return json
    })
    .catch((error) => {
      log.error("APIV2 CMS Error in", key, "as", error)
      throw error
    })
}

export async function _apiV2_get(url: string, options: ApiV2OptionsProps) {
  return _apiV2({ url, options: { ...options, method: "GET" } })
}

export async function _apiV2_post(url: string, options: ApiV2OptionsProps) {
  return _apiV2({ url, options: { ...options, method: "POST" } })
}

export async function _apiV2_put(url: string, options: ApiV2OptionsProps) {
  return _apiV2({ url, options: { ...options, method: "PUT" } })
}

export async function _apiV2_delete(url: string, options: ApiV2OptionsProps) {
  return _apiV2({ url, options: { ...options, method: "DELETE" } })
}

export * from "@/app/api2/ai_api"
export * from "@/app/api2/category_industry_api"
export * from "@/app/api2/custom_lists"
export * from "@/app/api2/filters_api"
export * from "@/app/api2/fund_firm_api"
export * from "@/app/api2/metrics_api"
export * from "@/app/api2/notes_api"
export * from "@/app/api2/oppfinder_api"
export * from "@/app/api2/pulses_api"
export * from "@/app/api2/term_api"
export * from "@/app/api2/user_api"
export * from "@/app/api2/viz_api"
